<template>
  <div class="grid">
    <DataTable
      v-if="rows.length > 0"
      :columnsConfig="columnsConfig"
      :data="rows"
      class="table-container fixed-header-table"
    >
      <template v-slot:cell="{ content, row, col, sub }">
        <template v-if="columnsConfig[col].key === 'categories'">
          <template v-for="(category, index) in content">
            <div :key="index">{{ category.name }}</div>
          </template>
        </template>
        <template v-else-if="columnsConfig[col].key === 'approvalStatus'">
          <StatusIcons
            :item-name="statusesItemsName"
            :status="content"
            @showToolTip="showToolTip"
            @hideToolTip="hideToolTip"
          />
        </template>
        <template
          v-else-if="includes(['ticker', 'secID'], columnsConfig[col].key) && sub === undefined"
        >
          <div
            class="target-series fund-serie__with-series-items"
            v-if="rows[row].__isTargetSeries"
          >
            {{ '' }}
          </div>
        </template>
        <template v-else-if="columnsConfig[col].key === 'displayName' && sub === undefined">
          <div
            class="target-series fund-serie__with-series-items"
            v-if="rows[row].subs.length > 0"
          >
            <CollapseButton
              :title="'Open/Close Fund Series'"
              :collapsed="rows[row].__hidden"
              :data="{rowIndex: row}"
              @toggle="toggleFundSeries(row)"
            >
              <span class="fund-series-name">
                {{ content }}
                <mds-icon class="pending-series-icon" name="clock-back" v-if="rows[row].pendingSeriesChange"></mds-icon>
              </span>
            </CollapseButton>
          </div>
          <div class="fund-series" v-else>
            <span>{{ content }}</span>
            <mds-tag class="lcsc" v-if="rows[row].isLCSC">Lowest Cost</mds-tag>
          </div>
        </template>
        <template v-else-if="columnsConfig[col].key === 'displayName'">
          <div class="target-series fund-series_item">
            {{ content }}
          </div>
          <mds-tag class="lcsc" v-if="rows[row].isLCSC">Lowest Cost</mds-tag>
        </template>
        <template v-else-if="columnsConfig[col].key === 'pending'">
          <template v-if="content">
            <mds-button
              class="button-icon"
              variation="icon-only"
              icon="clock-back"
              type="button"
              @mouseover="showToolTip($event, ['Pending'])"
              @mouseleave="hideToolTip"
            >
            </mds-button>
          </template>
          <template v-else>
            {{ '' }}
          </template>
        </template>
        <template v-else-if="columnsConfig[col].key === 'action'">
          <template v-if="content">
            <span
              v-if="mustShowAction(content, row, sub)"
              class="grid-universe__action-icon"
              :class="content"
              @click.prevent="actionsSchema[content].action(row, sub)"
            >
              <BaseGridIcon
                class="mds-icon grid-universe-button_fund-change-status"
                aria-hidden="true"
                :icon="actionsSchema[content].icon"
                :tooltipMessage="getTooltipMessage(content)"
                @showToolTip="showToolTip"
                @hideToolTip="hideToolTip"
              />
            </span>
            <span
              v-else-if="mustShowActionMenu(content, row, sub) && isOneClientView"
              class="grid-universe__action-icon"
              :class="content"
            >
              <contextual-menu
                class="grid-universe-menu__fund-settings"
                position="bottom-left"
                text="Manage Fund"
                :options="formatActionChoices(row, sub)"
                @selected="optionsSettingSelected"
              >
                <BaseGridIcon
                  slot="clickTrigger"
                  data-mds-popover="mds-popover"
                  aria-haspopup="true"
                  aria-expanded="true"
                  aria-controls="mds-popover--8809"
                  role="button"
                  class="mds-icon grid-universe-button__fund-settings"
                  aria-hidden="true"
                  :icon="actionsSchema[content].icon"
                  :tooltipMessage="getTooltipMessage(content)"
                  @showToolTip="showToolTip"
                  @hideToolTip="hideToolTip"
                />
              </contextual-menu>
            </span>
            <span v-else>
              {{ '' }}
            </span>
          </template>
          <template v-else>
            {{ '' }}
          </template>
        </template>
        <template v-else-if="columnsConfig[col].key === 'preferred'">
          <template v-if="mustShowPreferred(columnsConfig[col].key, row, sub)">
            <span
              class="grid-universe__preferred-icon"
              role="button"
              :class="{ star: content }"
              @click.prevent="actionsSchema.preferred.action(row, sub)"
            >
              <BaseGridIcon
                class="mds-icon grid-universe-button__preferred"
                aria-hidden="true"
                :icon="rows[row].preferredIcon"
                :tooltipMessage="actionsSchema.preferred.tooltipMessage"
                @showToolTip="showToolTip"
                @hideToolTip="hideToolTip"
              />
            </span>
          </template>
          <template v-else>
            {{ '' }}
          </template>
        </template>
        <template v-else>
          {{ content || "—" }}
        </template>
      </template>
    </DataTable>
    <div class="loading-spinner" v-if="isLoading">
      <mds-loader />
    </div>
    <p v-else-if="totalRows === 0" class="title-message">{{ emptyMessage }}</p>
  </div>
</template>
<script>
import {
  cloneDeep, concat, debounce, filter, find, findIndex, get, includes, merge, some,
} from 'lodash';
import slugify from 'slugify';
import { mapActions, mapState } from 'vuex';

import { MdsTag } from '@mds/tag';
import StatusIcons from '@/components/atoms/StatusIcons';
import BaseGridIcon from '@/components/atoms/BaseGridIcon';
import CollapseButton from '@/components/atoms/CollapseButton';
import ContextualMenu from '@/components/molecules/ContextualMenu';
import DataTable from '@/components/molecules/data-table/table';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import * as UniverseFundsService from '@/services/UniverseFunds';
import gridUniverserMessages from '@/config/grid-universe-messages.json';
import axios from 'axios';

const defaultColumnsConfig = require('@/config/grid-universe-columns.json');

const columnsConfig = cloneDeep(defaultColumnsConfig);

export default {
  components: {
    DataTable,
    MdsTag,
    StatusIcons,
    BaseGridIcon,
    CollapseButton,
    ContextualMenu,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'Organisms/GridUniverseGrid' }),
  ],
  props: {
    filters: {
      type: Object,
      required: true,
    },
    scrolled: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      columnsConfig,
      messages: Object.freeze(gridUniverserMessages),
      rows: [],
      currentPage: 1,
      totalPages: 1,
      requestController: null,
      requestUrl: '',
      queryInProcess: null,
      sortKey: 'displayName',
      sortColumn: 'fundname',
      abortRequestKey: 'AbortRequest',
      sortDirection: 1,
      actionsSchema: {
        empty: {
          icon: '',
          action: null,
          tooltipMessage: '',
        },
        approve: {
          icon: 'check',
          action: this.approve,
          tooltipMessage: 'Approve',
        },
        'change-status': {
          icon: 'caret-circle-down',
          action: this.changeStatus,
          tooltipMessage: 'Change Status',
        },
        'manage-series': {
          icon: 'caret-circle-down',
          action: this.manageSeries,
          tooltipMessage: 'Manage Series',
        },
        'add-fund-to-series': {
          icon: 'caret-circle-down',
          action: this.addFundToSeries,
          tooltipMessage: 'Add Fund to Series',
        },
        preferred: {
          icon: 'ip-star-rating',
          action: this.preferred,
          tooltipMessage: 'Preferred Fund',
        },
      },
      additionalColumns: {
        newSales: {
          text: 'New Sales',
          key: 'newSales',
          sortColumn: 'opennewsales',
          width: 76,
          sortDirection: 0,
        },
        preferred: {
          text: 'Preferred',
          key: 'preferred',
          sortColumn: 'preferred',
          width: 64,
          sortDirection: 0,
        },
      },
    };
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      selectedClientName: (state) => state.grid.client.name,
      selectedProduct: (state) => state.grid.product.selected,
      selectedTab: (state) => state.grid.tab,
      searchTerm: (state) => state.grid.searchTerm,
      totalRows: (state) => state.grid.totalRows,
      fullscreenModalShow: (state) => state.fullscreenModal.show,
      fullscreenModalPerformAction: (state) => state.fullscreenModal.performAction,
      config: (state) => state.system.config,
      isLoading: (state) => state.system.isLoading,
    }),
    emptyMessage() {
      if (this.searchTerm) {
        return this.messages.search.emptyFundsList;
      }
      return this.messages.emptyFundsList;
    },
    isLogoutTheCurrentRoute() {
      return this.$route.name === 'logout';
    },
    statusesItemsName() {
      if (this.isOneClientView) {
        return {
          singular: 'fund',
          plural: 'funds',
        };
      }
      return {
        singular: 'client',
        plural: 'clients',
      };
    },
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      // Tabs are applied to 1 Client View
      if (this.currentViewName === 'all-clients') {
        return get(this.policies, `grid.views.${this.currentViewName}`);
      }
      return get(this.policies, `grid.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    queryOptions() {
      const {
        grid: {
          pagination: { itemsPerPage },
        },
      } = this.config;
      const column = this.sortColumn;
      const direction = this.sortDirection === 1 ? 'asc' : 'desc';

      let parameters = `&pageSize=${itemsPerPage}&sort=${column}:${direction}`;

      if (this.filters.status.length > 0) {
        const escapedParameters = this.filters.status.map(
          (item) => encodeURIComponent(item.toLowerCase()),
        );
        parameters += `&status=${escapedParameters}`;
      }
      if (this.filters.categories.length > 0) {
        const escapedParameters = this.filters.categories.map((item) => encodeURIComponent(item));
        parameters += `&category=${escapedParameters}`;
      }
      if (this.filters.assets.length > 0) {
        const escapedParameters = this.filters.assets.map((item) => encodeURIComponent(item));
        parameters += `&broadassetclass=${escapedParameters}`;
      }
      if (this.searchTerm) {
        const escapedParameters = encodeURIComponent(this.searchTerm);
        parameters += `&search=${escapedParameters}`;
      }
      return parameters;
    },
    filterStatusSize() {
      return this.filters.status.length;
    },
    filterCategoriesSize() {
      return this.filters.categories.length;
    },
    filterAssetsSize() {
      return this.filters.assets.length;
    },
    preferredColumn() {
      return this.config.grid.columns.preferred;
    },
    isOneClientView() {
      return this.selectedClient !== 0 && this.selectedClientName !== 'All Clients';
    },
  },
  mounted() {
    // Load initial Data
    this.setIsLoading(true);
    this.loadData();

    this.debouncedSearchFunds = debounce(() => {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      return this.loadData();
    }, 700);
  },
  methods: {
    ...mapActions([
      'logError',
      'setIsLoading',
      'setSelectedFundToBeAdded',
      'cleanSelectedFundsList',
    ]),
    includes,
    cleanRequest() {
      this.requestController = null;
    },
    loadData() {
      if (this.requestController) {
        this.requestController.cancel();
      }

      this.setIsLoading(true);
      this.requestController = axios.CancelToken.source();
      UniverseFundsService.getUniverseFundsList({
        selectedClient: this.selectedClient,
        selectedFiduciary: this.selectedFiduciary,
        currentPage: this.currentPage,
        queryOptions: this.queryOptions,
        selectedProduct: this.selectedProduct,
        axiosCancelToken: this.requestController?.token,
      }).then((result) => {
        this.setIsLoading(true);
        if (get(result, 'status.success')) {
          const { totalItems = 0, totalPages = 1 } = result && result.pagination;
          this.totalPages = totalPages;
          if (result.data.length > 0) {
            const data = this.formatData(result.data);
            if (this.currentPage === 1) {
              this.rows = data;
            } else {
              this.rows = concat(this.rows, data);
            }
          }

          this.$store.dispatch('setTotalRows', totalItems);
        }
        this.setIsLoading(false);
      }).catch((error) => {
        const httpStatus = (error?.response?.status) ? (error?.response?.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/login');
        } else if (error.message && error.message !== this.abortRequestKey) {
          this.notifyError({
            error,
            section: 'methods:loadData',
            data: { url: this.requestUrl },
            notificaction: {
              title: 'Universe Grid',
              message: `Fetching Data (Error: ${error.message})`,
            },
          });
          this.setIsLoading(false);
        }
      }).finally(() => {
        this.cleanRequest();
      });
    },
    approve(row) {
      this.$store.dispatch('openFullscreenModal', {
        title: 'Approve Fund',
        component: 'approve',
        data: this.rows[row],
      });
    },
    optionsSettingSelected(selectedOption) {
      const actionSchema = get(this.actionsSchema, selectedOption.data.actionId);
      actionSchema.action(selectedOption.data.row, selectedOption.data.subRow);
    },
    changeStatus(row, subRow) {
      let data = this.rows[row];
      if (undefined !== subRow) {
        data = get(this.rows[row], `subs[${subRow}]`, {});
      }
      this.$store.dispatch('openFullscreenModal', {
        title: 'Change Status',
        component: 'change-status',
        data,
      });
    },
    // eslint-disable-next-line
    manageSeries(row, subRow) {
      const fundData = get(this.rows, `[${row}]`, {});
      const fundNSeriesId = String(fundData.id).split('-');
      const fundSeriesId = fundNSeriesId[1] ?? fundData.id;
      this.$router.push({ name: 'manageSeries', params: { clientId: +this.selectedClient, seriesId: +fundSeriesId } });
    },
    /**
     * Add fund to a series
     * @param row
     * @param subRow
     */
    addFundToSeries(row) {
      if (this.isOneClientView) {
        this.cleanSelectedFundsList();
        const fundData = get(this.rows, `[${row}]`, {});
        fundData.selectedClient = {
          id: this.selectedClient,
          name: this.selectedClientName,
        };
        this.setSelectedFundToBeAdded(fundData);
        this.$router.push({ name: 'addFundToSeries', params: { fundSlug: fundData.slug } });
      }
    },
    /**
     * Set/Unset preferred funds
     * @param row
     * @param subRow
     */
    preferred(row, subRow) {
      if (this.queryInProcess === true) {
        return false;
      }

      const data = this.rows[row];
      let processData = {
        displayName: null,
        preferred: undefined,
        payload: null,
        error: false,
      };

      if (data.__isTargetSeries && this.isOneClientView) {
        processData = this.getPayloadInfoToTagPreferredTargetSeries(row);
      } else {
        processData = this.getPayloadInfoToTagPreferredFund(row, subRow);
      }

      if (!processData.error) {
        this.queryInProcess = true;
        // Optimistic Change Strategy
        this.handleChangePreferredProcess(row, processData.displayName, processData.preferred, false);

        UniverseFundsService.tagPreferredFunds({
          selectedFiduciary: this.selectedFiduciary,
          selectedClient: this.selectedClient,
          currentPage: this.currentPage,
          queryOptions: this.queryOptions,
          selectedProduct: this.selectedProduct,
          secID: processData.secID,
          isTargetSeries: data.__isTargetSeries,
          payload: processData.payload,
        }).then((result) => {
          if (get(result, 'status.success')) {
            this.handleChangePreferredProcess(row, processData.displayName, processData.preferred);
          } else {
            // If no success we need to rollback the Optimistic Change Strategy
            this.handleChangePreferredProcess(row, processData.displayName, !processData.preferred, false);
            this.handleErrorFundProcess(
              'There has been an error when trying to mark as preferred the following',
              row,
              processData.displayName,
            );
          }
          this.queryInProcess = false;
        }).catch((error) => {
          // If error we need to rollback the Optimistic Change Strategy
          this.handleChangePreferredProcess(row, processData.displayName, !processData.preferred, false);
          const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;

          let errorMsg = `Error when trying to mark as a Preferred Fund to ${processData.displayName}.
          (Error: ${error.message})`;

          this.queryInProcess = false;
          if (httpStatus === 401) {
            !this.isLogoutTheCurrentRoute
              && this.$router.push({ name: 'logout' });
          } else if (httpStatus === 422) {
            // eslint-disable-next-line max-len
            errorMsg = "Funds having none/removed status cannot be marked/unmarked as preferred if they don't have any pending status";
          }
          this.notifyError({
            error,
            section: 'methods:preferred',
            data: { url: '', data, processData },
            notificaction: {
              title: 'Universe Grid Set Preferred Fund',
              message: errorMsg,
            },
          });
        }).finally(() => {
          this.queryInProcess = false;
        });
        return true;
      }
      return false;
    },
    getPayloadInfoToTagPreferredTargetSeries(row) {
      const data = this.rows[row];
      const { displayName: seriesDisplayName, preferred: seriesPreferred } = data;
      const fundSeries = data.subs;
      if (Array.isArray(fundSeries) && fundSeries.length > 0) {
        // Array of SecIds of the fund series
        const secIds = fundSeries.map((fund) => {
          const { secID } = fund;
          return secID;
        });

        const payloadInfo = {
          displayName: seriesDisplayName,
          preferred: seriesPreferred,
          payload: {
            isPreferred: !seriesPreferred,
            secIds,
          },
          error: false,
        };

        return { ...payloadInfo };
      }
      return {
        payload: null,
        error: true,
      };
    },
    getPayloadInfoToTagPreferredFund(row, subRow) {
      let data = this.rows[row];

      if (undefined !== subRow) {
        data = get(this.rows[row], `subs[${subRow}]`, {});
      }
      const { displayName, secID, preferred } = data;
      const requestBody = {
        isPreferred: !preferred,
      };

      if (this.selectedClient !== 0) {
        requestBody.secIds = [secID];
      } else {
        requestBody.secIds = undefined;
      }

      let payloadInfo = {
        displayName,
        preferred,
        payload: null,
        error: true,
      };

      if (secID) {
        payloadInfo = {
          secID,
          displayName,
          preferred,
          payload: requestBody,
          error: false,
        };
      }

      return { ...payloadInfo };
    },
    handleErrorFundProcess(errorMessage, row, displayName) {
      this.$emit('hideToolTip');

      const data = this.rows[row];
      const fundIdentifier = data.__isTargetSeries ? 'target series' : 'fund';
      const clientName = this.currentViewName === 'all-clients' ? 'All Clients' : this.selectedClientName;
      const title = `${errorMessage} ${fundIdentifier} for ${clientName}`;

      this.$store.dispatch('openNotification', {
        notificationType: 'error',
        title,
        message: displayName,
      });
    },
    handleChangePreferredProcess(row, displayName, preferred, withNotification = true) {
      this.$emit('hideToolTip');

      const data = {
        ...this.rows[row],
      };
      const fundIdentifier = data.__isTargetSeries ? 'target series' : 'fund';
      const clientName = this.currentViewName === 'all-clients' ? 'All Clients' : this.selectedClientName;
      const markAs = !preferred ? 'now' : 'no longer';
      const title = `The following ${fundIdentifier} is ${markAs} a preferred fund for ${clientName}`;

      if (withNotification) {
        this.$store.dispatch('openNotification', {
          title,
          message: displayName,
        });
      }

      data.preferred = !preferred;
      const { preferredIcon } = this.formatPreferred(data);
      data.preferredIcon = preferredIcon;

      this.$set(this.rows, row, data);
    },
    setSortBy() {
      this.columnsConfig = this.columnsConfig.map((col) => {
        const clonedCol = cloneDeep(col);
        if (col.key === this.sortKey) {
          clonedCol.sortDirection = this.sortDirection;
        } else if (col.sortDirection !== undefined) {
          clonedCol.sortDirection = 0;
        }
        return clonedCol;
      });
    },
    formatData(data) {
      // TODO Preferred Column
      let disableNone = false;

      // Reset columns
      this.columnsConfig = cloneDeep(defaultColumnsConfig);
      this.setSortBy();

      if (this.currentViewName === 'all-clients') {
        disableNone = true;
      } else {
        disableNone = false;

        // Add New Sales Column before Status column
        this.columnsConfig.splice(-3, 0, this.additionalColumns.newSales);
      }

      // Preferred column
      if (this.preferredColumn) {
        // Add Preferred column before Action column
        this.columnsConfig.splice(-1, 0, this.additionalColumns.preferred);
      }

      this.columnsConfig = this.columnsConfig.map((col) => {
        const clonedCol = cloneDeep(col);
        if (clonedCol.sortDirection !== undefined) {
          clonedCol.action = () => {
            if (this.sortKey === clonedCol.key) {
              this.sortDirection *= -1;
            } else {
              this.sortDirection = 1;
            }
            this.sortKey = clonedCol.key;
            this.sortColumn = clonedCol.sortColumn;
            this.setSortBy();
          };
        }
        return clonedCol;
      });

      const formatedData = data
        .map((row, index) => {
          let newRow = {};
          try {
            const fundsSeries = get(row, 'fundSeries.seriesFunds', []);
            let fundRef = { ...row };
            if (fundsSeries && fundsSeries.length > 0) {
              fundRef = {
                ...row,
                ...find(fundsSeries, (fund) => row.id === fund.id),
              };
            }

            const fundID = row.fundID || fundRef.fundID;
            const isPreferred = row.isPreferred || fundRef.isPreferred;
            const psaCategory = row.psaCategory || [get(row, 'fundSeries.seriesCategory')];
            const morningstarBroadAssetClass = row.morningstarBroadAssetClass
              || fundRef.morningstarBroadAssetClass;
            const morningstarCategory = row.morningstarCategory || fundRef.morningstarCategory;

            const id = this.formatId(row);
            const isPending = this.formatIsPending(row);
            const pendingStatus = this.formatPendingStatuses(row);
            const fundStatus = this.formatFundStatuses(row);
            const isOpenToNewSales = this.formatOpenToNewSales(row);
            const displayName = this.formatDisplayName(row);
            const categories = this.formatCategories(psaCategory);
            const approvalStatus = this.formatApprovalStatus(
              row,
              fundStatus,
              pendingStatus,
              disableNone,
            );
            const action = this.formatActions(fundStatus, pendingStatus);
            const slug = slugify(`${displayName}-${row.secID}`);

            newRow = {
              id,
              index,
              fundID,
              slug,
              displayName,
              ticker: row.ticker,
              secID: row.secID,
              categories,
              psaCategory,
              fundStatus,
              pendingStatus,
              morningstarBroadAssetClass,
              morningstarCategory,
              approvalStatus,
              isLCSC: row.isLCSC,
              pending: isPending,
              pendingSeriesChange: row.fundSeries ? row.fundSeries.isPendingSeriesChange : false,
              action,
              newSales: isOpenToNewSales,
              preferred: isPreferred,
              subs: fundsSeries || [],
              __isTargetSeries: this.isTargetSeries(row),
              __hidden: false,
            };

            newRow.subs = newRow.subs.map((fund) => {
              const fundStatusSub = get(fund, 'fundStatus');
              const {
                pendingStatus: pendingStatusSub,
                displayName: displayNameSub,
                secID: subSecID,
              } = fund;
              const approvalStatusSub = this.formatApprovalStatus(
                fund,
                fundStatusSub,
                pendingStatusSub,
                disableNone,
              );
              const subSlug = slugify(`${displayNameSub}-${subSecID}`);

              return {
                ...fund,
                slug: subSlug,
                fiduciaryFundID: fund.fiduciaryFundID || fund.id, // The API is unpredictable
                fundName: fund.displayName,
                preferred: fund.isPreferred,
                pending: fund.isPending,
                approvalStatus: approvalStatusSub,
                status: this.formatStatus(approvalStatusSub, fund),
                action: this.formatActions(fundStatusSub, pendingStatusSub),
                categories: this.formatCategories(fund.psaCategory),
                __isFundSeries: true,
                __hidden: false,
              };
            });

            // if there is no psaCategory, replace it with morningstarCategory
            if (newRow.categories && newRow.categories.length === 0) {
              newRow.categories = [row.morningstarCategory];
            }

            if (this.preferredColumn) {
              const { canBeMarkAsPreferred, preferredIcon } = this.formatPreferred(newRow);
              newRow.canBeMarkAsPreferred = canBeMarkAsPreferred;
              newRow.preferredIcon = preferredIcon;
            }
          } catch (e) {
            this.logError({
              data: row,
              errorFrom: 'GridUniverseGrid:methods:formatData',
              details: {
                ...Object.getOwnPropertyDescriptors(e),
                message: e.message,
              },
            });
          }
          return newRow;
        })
        .filter((row) => row.approvalStatus && row.approvalStatus.length > 0);
      return formatedData;
    },
    isTargetSeries(fund) {
      const fundsSeries = get(fund, 'fundSeries.seriesFunds', null);
      return !!fundsSeries && Array.isArray(fundsSeries);
    },
    formatId(data) {
      const fundSeries = get(data, 'fundSeries');
      let { id } = data;
      if (fundSeries && this.isOneClientView) {
        id = `${id}-${fundSeries.id}`;
      }
      return id;
    },
    formatIsPending(data) {
      // TODO: this property will be implemented in the API later and
      // this FE implementation must change
      const fundSeries = get(data, 'fundSeries.seriesFunds');
      let { isPending } = data;
      if (!isPending) {
        isPending = some(fundSeries, { isPending: true });
      }
      return isPending;
    },
    formatOpenToNewSales(data) {
      const fundSeries = get(data, 'fundSeries.seriesFunds');
      let { isOpenToNewSales } = data;
      if (!isOpenToNewSales) {
        isOpenToNewSales = some(fundSeries, { isOpenToNewSales: true });
      }
      return isOpenToNewSales;
    },
    formatPendingStatuses(data) {
      let pendingStatuses = data.pendingStatus;
      if (this.isOneClientView && this.isTargetSeries(data)) {
        const fundSeries = get(data, 'fundSeries.seriesFunds');
        pendingStatuses = [];
        // This process is necessary because all the pending status come inside an array.
        // Then we have an array of fund pending statuses per fund series &
        // I need an array of status.
        fundSeries
          .filter((fund) => fund.pendingStatus !== null)
          .map(
            (fund) => Array.isArray(fund.pendingStatus)
              && fund.pendingStatus.map((status) => pendingStatuses.push(status)),
          ) || [];
      }
      return pendingStatuses;
    },
    formatFundStatuses(data) {
      let fundStatuses = data.fundStatus;
      if (this.isOneClientView && this.isTargetSeries(data)) {
        const fundSeries = get(data, 'fundSeries.seriesFunds');
        fundStatuses = [];
        // This process is necessary because all the fund status come inside an array.
        // Then we have an array of fund statuses per fund series & I need an array of status.
        fundSeries
          .filter((fund) => fund.fundStatus !== null || fund.pendingStatus !== null)
          .map((fund) => {
            if (Array.isArray(fund.pendingStatus)) {
              // Pending statuses have priority
              return fund.pendingStatus.map((status) => fundStatuses.push(status));
            }
            return fund.fundStatus.map((status) => fundStatuses.push(status));
          }) || [];
      }
      return fundStatuses;
    },
    formatDisplayName(data) {
      const fundSeries = get(data, 'fundSeries');
      let { displayName } = data;
      if (fundSeries && this.isOneClientView) {
        displayName = fundSeries.seriesName;
      }
      return displayName;
    },
    formatCategories(categories = []) {
      const list = (categories
          && Array.isArray(categories)
          && categories.map((item) => ({ name: get(item, 'category.name', '') })))
        || [];

      return this.formatToWeightedList(list, () => true);
    },
    formatStatus(approvalStatusArray, fund) {
      let status = get(fund, 'fundStatus.fundStatus');
      if (Array.isArray(approvalStatusArray) && approvalStatusArray.length > 0) {
        const approvalStatus = approvalStatusArray[0];
        status = {
          id: get(approvalStatus, 'id', 0),
          name: get(approvalStatus, 'name', ''),
        };
      }
      return status;
    },
    formatApprovalStatus(rowData, currentData, pendingData, disableNone) {
      // If has pendingStatus override production Status
      let data = currentData;
      if (this.isTargetSeries(rowData) && this.isOneClientView) {
        // In this case, we already have the pending statuses prioritized in the currentData array
        data = merge(currentData || [], pendingData || []);
      } else if (pendingData && Array.isArray(pendingData) && pendingData.length > 0) {
        data = pendingData;
      }

      const list = (Array.isArray(data)
          && data.map((item) => ({
            id: get(item, 'fundStatus.id', 0),
            name: get(item, 'fundStatus.name', ''),
          })))
        || [];

      // If the value returned here is an empty array this row won't be shown
      return this.formatToWeightedList(list, (el) => el.id !== 4 || disableNone === false);
    },
    formatActions(currentData, pendingData) {
      // If has pendingStatus override production Status
      const data = (pendingData && pendingData.length > 0 ? pendingData : currentData) || [];

      // Get all statusses for Approve (None or Removed)
      const forApprove = filter(
        data,
        (o) => o.fundStatus && (o.fundStatus.name === 'Removed' || o.fundStatus.name === 'None'),
      );

      // If statusses are for Approve else Change Status
      let action = '';
      if (forApprove.length === data.length) {
        action = 'approve';
      } else {
        action = 'change-status';
      }

      // If this action is not allowed assign empty
      if (!this.localPolicyOptions.actions[action]) {
        action = 'empty';
      }
      return action;
    },
    formatPreferred(rawData) {
      const data = { ...rawData };
      const canBeMarkAsPreferred = data
        && data.approvalStatus
        && data.approvalStatus.some((item) => item.name === 'Approved' || item.name === 'OnWatch');
      const preferredIcon = canBeMarkAsPreferred && !data.preferred
        ? 'ip-star-rating-hypo'
        : this.actionsSchema.preferred.icon;
      return {
        canBeMarkAsPreferred,
        preferredIcon,
      };
    },
    formatToWeightedList(list = [], include, property = 'name') {
      // Group by ocurrence
      const weightedList = [];
      list.forEach((element) => {
        if (include(element)) {
          const index = findIndex(weightedList, [property, element[property]]);
          if (index === -1) {
            weightedList.push({
              [property]: element[property],
              id: element.id,
              value: 1,
            });
          } else {
            weightedList[index].value += 1;
            weightedList[index].id = element.id;
          }
        }
      });

      // Order by mayor ocurrence
      weightedList.sort((a, b) => {
        if (a.value > b.value) {
          return -1;
        }
        if (a.value < b.value) {
          return 1;
        }
        if (a.value === b.value && a[property].toLowerCase() < b[property].toLowerCase()) {
          return -1;
        }
        if (a.value === b.value && a[property].toLowerCase() > b[property].toLowerCase()) {
          return 1;
        }
        return 0;
      });

      return weightedList;
    },
    formatActionChoices(row, subRow) {
      const rowData = this.rows[row];

      let options = [
        {
          id: `change-status-${row}-${subRow || 'x'}`,
          disabled: false,
          text: this.actionsSchema['change-status'].tooltipMessage,
          data: {
            actionId: 'change-status',
            row,
            subRow,
            rowData,
          },
        },
        {
          id: `add-fund-to-series-${row}-${subRow || 'x'}`,
          disabled: false,
          text: this.actionsSchema['add-fund-to-series'].tooltipMessage,
          data: {
            actionId: 'add-fund-to-series',
            row,
            subRow,
            rowData,
          },
        },
      ];
      if (rowData.__isTargetSeries) {
        options = [
          {
            id: `change-status-${row}-${subRow || 'x'}`,
            disabled: false,
            text: this.actionsSchema['change-status'].tooltipMessage,
            data: {
              actionId: 'change-status',
              row,
              subRow,
              rowData,
            },
          },
          {
            id: `manage-series-${row}-${subRow || 'x'}`,
            disabled: false,
            text: this.actionsSchema['manage-series'].tooltipMessage,
            data: {
              actionId: 'manage-series',
              row,
              subRow,
              rowData,
            },
          },
        ];
      }
      return options;
    },
    mustShowAction(content, row, subRow) {
      let data = this.rows[row];
      if (undefined !== subRow) {
        data = get(this.rows[row], `subs[${subRow}]`, {});
      }
      return (
        (content === 'change-status' || content === 'approve')
        // In the one client view we only show the gear icon with the fund
        //  setting menu that already includes the "Change Status" option
        && !(content === 'change-status' && this.isOneClientView)
        // In the one client view there shouldn't be displayed the approve icon at
        //  target series level, only at fund series level
        && !(content === 'approve' && data.__isTargetSeries && this.isOneClientView)
      );
    },
    mustShowActionMenu(content, row, subRow) {
      let data = this.rows[row];
      if (undefined !== subRow) {
        data = get(this.rows[row], `subs[${subRow}]`, {});
      }
      return content === 'change-status' && !(content === 'change-status' && data.__isFundSeries);
    },
    mustShowPreferred(key, row, subRow) {
      let data = this.rows[row];
      if (undefined !== subRow) {
        data = get(this.rows[row], `subs[${subRow}]`, {});
      }
      return (
        (!(key === 'preferred' && data.__isFundSeries) || !this.isOneClientView)
        && data.canBeMarkAsPreferred
      );
    },
    showToolTip(event, message) {
      this.$emit('showToolTip', event, message, undefined, 'small');
    },
    hideToolTip() {
      this.$emit('hideToolTip');
    },
    getTooltipMessage(content) {
      if (this.isOneClientView && content === 'change-status') {
        return 'Manage Fund';
      }
      return this.actionsSchema[content].tooltipMessage;
    },
    toggleFundSeries(index) {
      if (this.rows[index] && this.rows[index].subs) {
        this.rows[index].__hidden = !this.rows[index].__hidden;
        this.rows[index].subs = this.rows[index].subs.map((fund) => ({
          ...fund,
          __hidden: !fund.__hidden,
        }));
      }
    },
  },
  watch: {
    selectedClient() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    selectedProduct() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    filterStatusSize() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    filterCategoriesSize() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    filterAssetsSize() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    sortColumn() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    sortDirection() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = 1;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    searchTerm() {
      this.debouncedSearchFunds();
    },
    scrolled(value) {
      if (value && !this.isLoading && this.currentPage < this.totalPages) {
        this.currentPage += 1;
        this.setIsLoading(true);
        this.loadData();
      }
    },
    fullscreenModalShow(newValue, oldValue) {
      const closed = oldValue === true && newValue === false;
      if (closed && this.fullscreenModalPerformAction) {
        this.$store.dispatch('setTotalRows', 0);
        this.currentPage = 1;
        this.rows = [];
        this.setIsLoading(true);
        this.loadData();
      }
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.cancel(this.abortRequestKey);
    this.setIsLoading(false);
  },
};
</script>
<style lang="scss">
.grid-universe .grid {
  height: 1px; // Flex fix
  padding: 0 $mds-space-4-x;
  position: relative;
}
.grid-universe .mds-data-table {
  width: 100%;
  .button-icon {
    svg {
      @include mds-icon-color($mds-color-neutral-80);
    }
  }
}
.grid-universe .mds-data-table__header-cell {
  vertical-align: top;
}
.grid-universe .mds-data-table__header-cell[aria-sort] {
  cursor: pointer;
}
.grid-universe .mds-data-table__header-cell-inner {
  padding-top: $mds-space-1-x;
}
.grid-universe .mds-data-table__header-cell-inner[role="button"]:focus {
  box-shadow: none;
}
.grid-universe .mds-data-table__cell {
  vertical-align: middle;
}

// Specific Grid table styles
.grid-universe {
  .level-0:hover,
  .level-1:hover {
    box-shadow: 0 0 4px 0 rgba(70, 60, 60, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    .mds-data-table__cell {
      background: $mds-table-row-hover-color;
    }
  }
  .level-0:hover,
  .level-1:hover {
    &__undo .baseGridIcon {
      background-color: #e0f3fb;

      svg {
        @include mds-icon-color($mds-color-neutral-37);
      }
    }
  }
  .fund-series {
    padding: 0 $mds-space-3-x;
  }
  .fund-series-name {
    font-weight: bold;
    color: $mds-color-black;
  }
  .fund-series_item {
    padding: 0 $mds-space-5-x;
    line-height: 28px;
  }
  .mds-data-table {
    tr.mds-data-table__row {
      &.parent-row,
      &.child-row {
        background-color: $mds-color-neutral-95;
      }
    }
  }
}

// Icon common styles
.grid-universe {
  .mds-data-table__row .baseGridIcon {
    width: $mds-space-inset-4-x;
    height: $mds-space-inset-4-x;
    border-radius: 6px;

    svg {
      @include mds-icon-color($mds-color-neutral-80);
    }
  }
}

// Action icon
.grid-universe {
  &__action-icon:hover .baseGridIcon {
    background-color: #e0f3fb;

    svg {
      @include mds-icon-color($mds-color-neutral-37);
    }
  }
}

// Preferred icon
.grid-universe {
  .star .baseGridIcon svg {
    @include mds-icon-color($mds-interactive-color-primary-default);
  }

  &__preferred-icon:hover .baseGridIcon {
    background-color: #e0f3fb;

    svg {
      @include mds-icon-color($mds-color-neutral-37);
    }
  }
}

// Fix Header
.fixed-header-table {
  position: relative;
}
.fixed-header-table .mds-data-table__header th {
  background-color: $mds-color-white;
}
.fixed-header-table .mds-data-table__header th {
  position: sticky;
  top: 0;
  z-index: 100;
}

// Loader
.loading-spinner {
  padding-bottom: $mds-space-2-x;
}

.title-message {
  @include mds-level-1-heading();
  font-weight: $mds-font-weight-thin;
  color: $mds-text-color-primary-on-light;
  margin: $mds-space-8-x auto;
  text-align: center;
  width: 50%;
}

.pending-series-icon{
  padding: $mds-space-three-quarter-x 0 0 $mds-space-half-x;
}
</style>

<style lang="scss" module>
:global(.lcsc) {
  &.mds-tag {
    margin-left: $mds-space-1-x;
  }
}
</style>
